import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import { useMutation } from "@tanstack/react-query";
import { Link, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { ErrorBanner } from "../components/ErrorBanner.js";
import { track } from "../lib/analytics.js";
import { deviceSessionHeaders, prepareDeviceSession } from "../lib/deviceSession.js";
import { useTenant } from "../lib/tenant.js";

export function LoginPage() {
  const { config } = useTenant();
  const search = useSearch({ from: "/login" });
  const linkError = search.error === "invalid_link";
  const [email, setEmail] = useState("");

  // Stores with fraud screening on load the SEON agent early, so the device session is ready
  // by the time the shopper submits; the request never waits on it past a short budget.
  const screening = config.fraudScreening;
  useEffect(() => {
    if (screening) prepareDeviceSession();
  }, [screening]);

  // The BFF answers 202 whether or not the account exists, so success here
  // deliberately reveals nothing about the email.
  const magicLink = useMutation({
    mutationFn: async (to: string) => {
      const res = await fetch("/api/auth/magic-link", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          ...(await deviceSessionHeaders(screening)),
        },
        body: JSON.stringify({ email: to }),
      });
      if (!res.ok) throw new Error("could not send the sign-in link");
    },
    onSuccess: () => track("sign_in_requested", {}),
  });
  const sent = magicLink.isSuccess;
  const busy = magicLink.isPending;
  const error = magicLink.error
    ? magicLink.error instanceof Error
      ? magicLink.error.message
      : "something went wrong"
    : null;

  return (
    <div className="flex justify-center pt-12">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="text-lg font-bold tracking-tight">
            Sign in to {config.name}
          </CardTitle>
          <CardDescription>
            We'll email you a magic link — no password needed. Your pulls and balance persist to
            your account.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          {linkError && !sent && (
            <ErrorBanner>That sign-in link is invalid or expired — request a new one.</ErrorBanner>
          )}
          {error && <ErrorBanner>{error}</ErrorBanner>}

          {sent ? (
            <div className="grid gap-3 text-sm">
              <p>
                Check your email — we sent a sign-in link to{" "}
                <span className="font-medium">{email}</span>. It's valid for 15 minutes.
              </p>
              <Button variant="secondary" onClick={() => magicLink.reset()}>
                Use a different email
              </Button>
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                magicLink.mutate(email);
              }}
              className="grid gap-4"
            >
              <div className="grid gap-2">
                <Label htmlFor="login-email">Email</Label>
                <Input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={busy}>
                {busy ? "Sending…" : "Email me a sign-in link"}
              </Button>
              {/* One form signs up and signs in, so consent is a line under it rather than a
                  checkbox every returning shopper would have to tick. */}
              <p className="text-xs text-muted-foreground">
                By continuing you agree to the{" "}
                <Link to="/terms" className="underline">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to="/privacy" className="underline">
                  Privacy Policy
                </Link>
                , and confirm you are at least 18 years old.
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
